/**
 * TalentLiveStream
 *
 * Owns ALL Zego interactions: room join, stream creation, publishing.
 *
 * KARAOKE INTEGRATION (Sing4YOuManagement):
 *  Sing4YOuManagement builds a Web Audio mix (mic + instrumental) and passes
 *  the mixed MediaStreamTrack via the `mixedAudioTrack` prop.
 *  This component calls RTCRtpSender.replaceTrack() on its own captured
 *  RTCPeerConnection — the stream stays published, no re-login needed.
 *  On deactivate (mixedAudioTrack → null) the original mic track is restored.
 *
 * This eliminates the previous "not login" (1000002) error that occurred when
 * Sing4You tried to independently call startPublishingStream.
 */

import { useEffect, useRef, useState } from "react";
import {
  Box,
  IconButton,
  styled,
  useMediaQuery,
  useTheme,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Snackbar,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Tooltip,
} from "@mui/material";
import {
  Mic,
  MicOff,
  Videocam,
  VideocamOff,
  CallEnd,
  Settings,
  Hd,
  Fullscreen,
  FullscreenExit,
} from "@mui/icons-material";
import { ZegoExpressEngine } from "zego-express-engine-webrtc";
import { getZegoToken } from "../../../services/api/zego.api";

/* ─── RTCPeerConnection interceptor (module-level, single instance) ─────── */
// Monkey-patches window.RTCPeerConnection around zg.createStream() to capture
// Zego's internal PC so we can call getSenders() / replaceTrack() on it.
let _capturedPC: RTCPeerConnection | null = null;
let _originalRTCPC: typeof RTCPeerConnection | null = null;

function startInterceptingRTCPC() {
  _capturedPC = null;
  _originalRTCPC = window.RTCPeerConnection;
  const OrigPC = _originalRTCPC;
  function PatchedPC(this: RTCPeerConnection, ...args: ConstructorParameters<typeof RTCPeerConnection>) {
    // @ts-ignore
    const pc = new OrigPC(...args) as RTCPeerConnection;
    if (!_capturedPC) {
      _capturedPC = pc;
      console.log("[TalentLiveStream][PCInterceptor] Captured Zego RTCPeerConnection");
    }
    return pc;
  }
  PatchedPC.prototype = OrigPC.prototype;
  Object.setPrototypeOf(PatchedPC, OrigPC);
  (window as any).RTCPeerConnection = PatchedPC;
}

function stopInterceptingRTCPC() {
  if (_originalRTCPC) {
    window.RTCPeerConnection = _originalRTCPC;
    _originalRTCPC = null;
  }
}

function getCapturedPC(): RTCPeerConnection | null {
  return _capturedPC;
}

/* ─── Karaoke overlay constants ──────────────────────────────────────────── */
const KQ = ["#ff6b9d", "#22d3ee", "#a855f7", "#fbbf24"] as const;

const KQ_BARS = [
  { h: 20, dur: "0.38s", delay: "0.00s" }, { h: 32, dur: "0.45s", delay: "0.07s" },
  { h: 14, dur: "0.33s", delay: "0.14s" }, { h: 28, dur: "0.52s", delay: "0.03s" },
  { h: 22, dur: "0.41s", delay: "0.21s" }, { h: 36, dur: "0.35s", delay: "0.10s" },
  { h: 18, dur: "0.49s", delay: "0.28s" }, { h: 30, dur: "0.43s", delay: "0.05s" },
  { h: 26, dur: "0.38s", delay: "0.17s" }, { h: 12, dur: "0.56s", delay: "0.24s" },
  { h: 34, dur: "0.40s", delay: "0.09s" }, { h: 24, dur: "0.47s", delay: "0.32s" },
  { h: 16, dur: "0.36s", delay: "0.13s" }, { h: 30, dur: "0.53s", delay: "0.06s" },
  { h: 22, dur: "0.42s", delay: "0.19s" }, { h: 28, dur: "0.39s", delay: "0.26s" },
];

const KQ_NOTES = [
  { n: "♪", left: "6%",  dur: "4.0s", delay: "0.0s",  size: "1.4rem", col: "#ff6b9d" },
  { n: "♫", left: "19%", dur: "5.2s", delay: "1.0s",  size: "1.8rem", col: "#22d3ee" },
  { n: "♬", left: "33%", dur: "3.9s", delay: "1.8s",  size: "1.2rem", col: "#a855f7" },
  { n: "♩", left: "48%", dur: "4.7s", delay: "0.5s",  size: "1.6rem", col: "#fbbf24" },
  { n: "♪", left: "62%", dur: "5.1s", delay: "2.3s",  size: "1.3rem", col: "#ff6b9d" },
  { n: "♫", left: "76%", dur: "3.7s", delay: "1.2s",  size: "1.9rem", col: "#22d3ee" },
  { n: "♬", left: "88%", dur: "4.9s", delay: "2.9s",  size: "1.5rem", col: "#a855f7" },
];

/** First line: up to maxWords words; rest wrap below */
function formatTitleLines(title: string, maxWords = 5): React.ReactNode {
  if (!title || !String(title).trim()) return title;
  const words = String(title).trim().split(/\s+/).filter(Boolean);
  if (words.length <= maxWords) return title;
  const firstLine = words.slice(0, maxWords).join(" ");
  const rest = words.slice(maxWords).join(" ");
  return <>{firstLine}<br />{rest}</>;
}

/** Overlaid on the <video> element when karaoke is active */
function KaraokeVideoOverlay({ trackName }: { trackName: string }) {
  return (
    <Box sx={{
      position: "absolute", inset: 0, borderRadius: "inherit",
      overflow: "hidden", pointerEvents: "none", zIndex: 4,
    }}>

      {/* ── Stage spotlights (cones from top) ── */}
      <Box sx={{
        position: "absolute", top: 0, left: "0%", width: "45%", height: "55%",
        background: "radial-gradient(ellipse at 28% 0%, rgba(255,107,157,0.30) 0%, transparent 65%)",
        animation: "kSweepL 5s ease-in-out infinite alternate",
        "@keyframes kSweepL": { from: { opacity: 0.7 }, to: { opacity: 1 } },
      }} />
      <Box sx={{
        position: "absolute", top: 0, left: "20%", width: "60%", height: "65%",
        background: "radial-gradient(ellipse at 50% 0%, rgba(168,85,247,0.35) 0%, transparent 60%)",
        animation: "kPulseC 3s ease-in-out infinite",
        "@keyframes kPulseC": { "0%,100%": { opacity: 0.75 }, "50%": { opacity: 1 } },
      }} />
      <Box sx={{
        position: "absolute", top: 0, right: "0%", width: "45%", height: "55%",
        background: "radial-gradient(ellipse at 72% 0%, rgba(34,211,238,0.27) 0%, transparent 65%)",
        animation: "kSweepR 4.5s ease-in-out infinite alternate",
        "@keyframes kSweepR": { from: { opacity: 0.8 }, to: { opacity: 1 } },
      }} />

      {/* ── Floor glow ── */}
      <Box sx={{
        position: "absolute", bottom: 0, left: "10%", width: "80%", height: "30%",
        background: "radial-gradient(ellipse at 50% 100%, rgba(168,85,247,0.22) 0%, transparent 70%)",
        animation: "kFloor 4s ease-in-out infinite",
        "@keyframes kFloor": { "0%,100%": { opacity: 0.7 }, "50%": { opacity: 1 } },
      }} />

      {/* ── Floating music notes ── */}
      {KQ_NOTES.map((n, i) => (
        <Box key={i} sx={{
          position: "absolute", left: n.left, bottom: "15%",
          fontSize: n.size, color: n.col,
          textShadow: `0 0 10px ${n.col}`,
          animation: `kNoteFloat ${n.dur} ${n.delay} ease-in infinite`,
          "@keyframes kNoteFloat": {
            "0%":   { opacity: 0,   transform: "translateY(0)    scale(0.8)" },
            "15%":  { opacity: 0.9, transform: "translateY(-30px)  scale(1)"   },
            "85%":  { opacity: 0.6, transform: "translateY(-160px) scale(1.05)" },
            "100%": { opacity: 0,   transform: "translateY(-220px) scale(0.7)" },
          },
        }}>{n.n}</Box>
      ))}

      {/* ── Bottom bar: EQ + track name ── */}
      <Box sx={{
        position: "absolute", bottom: 0, left: 0, right: 0,
        pt: 5, pb: 1.5, px: 2,
        background: "linear-gradient(to top, rgba(0,0,0,0.94) 0%, rgba(0,0,0,0.75) 50%, transparent 100%)",
      }}>
        {/* Row 1: mic + KARAOKE MODE + EQ bars */}
        <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 0.6 }}>
          <Typography sx={{
            fontSize: "1.4rem", lineHeight: 1,
            animation: "kMicPulse 1.5s ease-in-out infinite",
            "@keyframes kMicPulse": {
              "0%,100%": { transform: "scale(1)",    filter: "drop-shadow(0 0 4px #ff6b9d)" },
              "50%":     { transform: "scale(1.2)",  filter: "drop-shadow(0 0 14px #ff6b9d)" },
            },
          }}>🎤</Typography>

          <Typography sx={{
            color: "#ff6b9d", fontWeight: 900,
            fontSize: { xs: "0.72rem", sm: "0.85rem" },
            letterSpacing: 3, textTransform: "uppercase",
            textShadow: "0 0 16px rgba(255,107,157,0.9)",
            animation: "kLabelGlow 2.5s ease-in-out infinite",
            "@keyframes kLabelGlow": {
              "0%,100%": { textShadow: "0 0 12px rgba(255,107,157,0.8)" },
              "50%":     { textShadow: "0 0 24px rgba(34,211,238,0.9)" },
            },
          }}>Karaoke Mode</Typography>

          <Box sx={{ flex: 1 }} />

          {/* EQ bars */}
          <Box sx={{ display: "flex", alignItems: "flex-end", gap: "2px", height: 28 }}>
            {KQ_BARS.map((b, i) => (
              <Box key={i} sx={{
                width: 4, borderRadius: "2px 2px 0 0",
                bgcolor: KQ[i % 4],
                boxShadow: `0 0 5px ${KQ[i % 4]}70`,
                transformOrigin: "bottom",
                animation: `kEQ ${b.dur} ${b.delay} ease-in-out infinite alternate`,
                height: `${b.h}px`,
                "@keyframes kEQ": {
                  from: { transform: "scaleY(0.2)", opacity: 0.5 },
                  to:   { transform: "scaleY(1)",   opacity: 1   },
                },
              }} />
            ))}
          </Box>
        </Box>

        {/* Row 2: track name */}
        {trackName && (
          <Typography sx={{
            color: "rgba(255,255,255,0.9)", fontWeight: 700,
            fontSize: { xs: "0.72rem", sm: "0.85rem" },
            minWidth: 0, wordBreak: "break-word",
            textShadow: "0 0 8px rgba(34,211,238,0.5)",
          }}>♪&nbsp;{formatTitleLines(trackName)}</Typography>
        )}
      </Box>

      {/* ── Top-right ON AIR pill ── */}
      <Box sx={{
        position: "absolute", top: 10, right: 10,
        display: "flex", alignItems: "center", gap: 0.6,
        bgcolor: "rgba(0,0,0,0.65)", border: "1px solid rgba(255,107,157,0.6)",
        borderRadius: 1.5, px: 1.2, py: 0.4,
      }}>
        <Box sx={{
          width: 7, height: 7, borderRadius: "50%", bgcolor: "#ff6b9d",
          boxShadow: "0 0 6px #ff6b9d",
          animation: "kOnAir 1.2s ease-in-out infinite",
          "@keyframes kOnAir": { "0%,100%": { opacity: 1 }, "50%": { opacity: 0.2 } },
        }} />
        <Typography sx={{ color: "#ff6b9d", fontSize: "0.68rem", fontWeight: 800, letterSpacing: 1 }}>
          ON AIR
        </Typography>
      </Box>
    </Box>
  );
}

/* ─── styled containers ───────────────────────────────────────────────────── */
const VideoCallWrapper = styled(Box)<{}>`
  display: flex;
  flex-direction: column;
  align-items: center;
  position: relative;
  width: 100%;
  transition: all 0.3s ease-in-out;
`;

const MainContainer = styled(Box)`
  display: flex;
  width: 100%;
  height: 100%;
  position: relative;
  width: 100%;
`;

/* ═══════════════════════════════════════════════════════════════════════════ */
export default function TalentLiveStream({
  userId,
  videoId,
  onCallEnd,
  onEndStream,
  zg,
  mixedAudioTrack = null,
  karaokeActive   = false,
  karaokeTrackName = "",
}: {
  userId: string;
  videoId: string;
  onCallEnd: () => void;
  onEndStream: () => void;
  zg: ZegoExpressEngine | null;
  mixedAudioTrack?: MediaStreamTrack | null;
  /** Passed from StageLinkTalentPage when Sing4You broadcasts a play event */
  karaokeActive?: boolean;
  karaokeTrackName?: string;
}) {
  const [micOn, setMic] = useState(true);
  const [cameraOn, setCamera] = useState(true);
  const [settingsAnchor, setSettingsAnchor] = useState<null | HTMLElement>(null);
  const [videoQuality, setVideoQuality] = useState<"240p" | "360p" | "480p" | "720p">("720p");
  const [qualityAuto, setQualityAuto] = useState(true);
  const [snackbar, setSnackbar] = useState<{
    open: boolean;
    message: string;
    severity: "success" | "warning" | "error";
  }>({ open: false, message: "", severity: "success" });
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [showEndDialog, setShowEndDialog] = useState(false);

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const [videoToken, setVideoToken] = useState<string | null>(null);

  const localVideoRef    = useRef<HTMLVideoElement>(null);
  const fullscreenRef    = useRef<HTMLDivElement>(null);
  const [localStream, setLocalStream] = useState<MediaStream | null>(null);
  const [isJoined, setIsJoined] = useState(false);

  // RTCPeerConnection and original mic track — captured once during createStream
  const capturedPCRef = useRef<RTCPeerConnection | null>(null);
  const originalMicTrackRef = useRef<MediaStreamTrack | null>(null);

  const streamId = "stream_" + userId;

  /* ─── replaceTrack + broadcast karaoke status to the room ──────────── */
  useEffect(() => {
    const pc = capturedPCRef.current;
    if (!pc) return;

    const audioSender = pc.getSenders().find(s => s.track?.kind === "audio");
    if (!audioSender) {
      console.warn("[TalentLiveStream] replaceTrack: no audio sender found in PC");
      return;
    }

    if (mixedAudioTrack) {
      audioSender.replaceTrack(mixedAudioTrack)
        .then(() => {
          console.log("[TalentLiveStream] ✅ replaceTrack: karaoke mix active");
          // Notify everyone in the room (host, audience) that karaoke started
          if (zg && isJoined) {
            zg.sendBroadcastMessage(
              videoId,
              JSON.stringify({
                type: "karaoke_status",
                data: { active: true, trackName: karaokeTrackName || undefined },
              })
            ).catch(() => {});
          }
        })
        .catch(e => console.warn("[TalentLiveStream] replaceTrack (mix) failed:", e));
    } else if (originalMicTrackRef.current?.readyState === "live") {
      audioSender.replaceTrack(originalMicTrackRef.current)
        .then(() => {
          console.log("[TalentLiveStream] ✅ replaceTrack: original mic restored");
          // Notify everyone in the room that karaoke stopped
          if (zg && isJoined) {
            zg.sendBroadcastMessage(
              videoId,
              JSON.stringify({ type: "karaoke_status", data: { active: false } })
            ).catch(() => {});
          }
        })
        .catch(e => console.warn("[TalentLiveStream] replaceTrack (restore) failed:", e));
    }
  }, [mixedAudioTrack, zg, isJoined, videoId]);

  /* ─── show local stream in <video> whenever it changes ──────────────── */
  useEffect(() => {
    const el = localVideoRef.current;
    if (!el || !localStream) return;
    el.srcObject = localStream;
    el.muted = true;
    el.play().catch(() => {});
  }, [localStream]);

  /* ─── Zego publisher state verifier (for DevTools confirmation) ─────── */
  useEffect(() => {
    if (!zg || !isJoined) return;

    const onPublisherStateUpdate = (updateInfo: any) => {
      const { streamID, state, errorCode } = updateInfo;
      if (streamID !== streamId) return;
      if (state === "PUBLISHING") {
        console.log("[TalentLiveStream][ZegoVerify] ✅ Own stream PUBLISHING ok", { streamID, errorCode });
      } else if (state === "NO_PUBLISH") {
        console.warn("[TalentLiveStream][ZegoVerify] ⚠️ Own stream NO_PUBLISH", { streamID, errorCode });
      }
    };

    zg.on("publisherStateUpdate", onPublisherStateUpdate);
    return () => {
      try { zg.off("publisherStateUpdate", onPublisherStateUpdate); } catch (_) {}
    };
  }, [zg, isJoined, streamId]);

  /* ─── join room ─────────────────────────────────────────────────────── */
  const joinRoom = async () => {
    try {
      if (isJoined || !zg || !videoToken) return;

      console.log("[TalentLiveStream] joinRoom — token present", { videoId, userId });

      await zg.loginRoom(
        videoId,
        videoToken,
        { userID: userId, userName: userId },
        { userUpdate: true }
      );
      setIsJoined(true);

      // Intercept RTCPeerConnection creation so we can replaceTrack later
      startInterceptingRTCPC();

      const stream = await zg.createStream({
        camera: {
          audio: micOn,
          video: cameraOn,
          videoQuality: 4,
          bitrate: 3000,
          frameRate: 30,
          width: 1280,
          height: 720,
        },
      });

      // Stop intercepting and grab the captured PC
      stopInterceptingRTCPC();
      await new Promise(r => setTimeout(r, 300)); // let Zego finish ICE setup
      capturedPCRef.current = getCapturedPC();
      console.log("[TalentLiveStream] Captured PC:", !!capturedPCRef.current,
        capturedPCRef.current ? `senders: ${capturedPCRef.current.getSenders().length}` : "none");

      // Save the original Zego mic track so we can restore it after karaoke
      originalMicTrackRef.current = stream.getAudioTracks()[0] ?? null;

      if (stream) {
        zg.startPublishingStream(streamId, stream);
        setLocalStream(stream); // triggers useEffect to show in <video>
        console.log("[TalentLiveStream] Own stream published", { streamId });
      }
    } catch (error) {
      stopInterceptingRTCPC();
      console.error("[TalentLiveStream] joinRoom error", error);
    }
  };

  /* ─── cleanup ────────────────────────────────────────────────────────── */
  /**
   * NOTE: We deliberately do NOT call zg.destroyEngine() here.
   * The engine instance is owned by the parent and shared with
   * Sing4YOuManagement. Destroying it would kill the karaoke mixed stream.
   */
  const cleanup = () => {
    if (zg && isJoined) {
      if (localStream) {
        localStream.getTracks().forEach((track) => track.stop());
        try { zg.stopPublishingStream(streamId); } catch (_) {}
        try { zg.destroyStream(localStream); } catch (_) {}
      }
      try { zg.logoutRoom(videoId); } catch (_) {}
      setIsJoined(false);
      setLocalStream(null);
      capturedPCRef.current = null;
      originalMicTrackRef.current = null;
    }
  };

  /* ─── initial join when token is ready ──────────────────────────────── */
  useEffect(() => {
    if (zg && videoToken) {
      joinRoom();
    }
    return () => { cleanup(); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [zg, videoToken]);

  /* ─── token fetch — always fresh, no stale localStorage ─────────────── */
  useEffect(() => {
    if (videoToken) return;
    getZegoToken(userId!)
      .then((token: string) => {
        console.log("[TalentLiveStream] Fresh Zego token acquired");
        setVideoToken(token);
      })
      .catch((err: any) => {
        console.error("[TalentLiveStream] Token fetch failed", err.message);
      });
  }, [videoId, userId]);

  /* ─── fullscreen listener + toggle ──────────────────────────────────── */
  useEffect(() => {
    const handler = () => setIsFullscreen(!!document.fullscreenElement);
    document.addEventListener("fullscreenchange", handler);
    return () => document.removeEventListener("fullscreenchange", handler);
  }, []);

  const toggleFullscreen = () => {
    if (isFullscreen) {
      document.exitFullscreen().catch(() => {});
    } else {
      fullscreenRef.current?.requestFullscreen().catch(() => {});
    }
  };

  /* ─── control button styles ──────────────────────────────────────────── */
  const btnSize = isMobile ? 36 : 42;
  const controlButtonStyle = {
    height: btnSize,
    width: btnSize,
    padding: isMobile ? "6px" : "8px",
    backgroundColor: "rgba(238,242,255,0.92)",
    color: "#000",
    borderRadius: "50%",
    zIndex: 3,
    backdropFilter: "blur(4px)",
  };

  const controlButtonStyleDestructive = {
    height: btnSize,
    width: btnSize,
    padding: isMobile ? "6px" : "8px",
    backgroundColor: "#E50914",
    color: "#fff",
    borderRadius: "50%",
    zIndex: 3,
  };

  /* ─── actions ────────────────────────────────────────────────────────── */
  const handleLeaveSession = () => {
    cleanup();
    onCallEnd();
    setShowEndDialog(false);
  };

  const handleEndSession = async () => {
    try {
      onEndStream();
      cleanup();
      setShowEndDialog(false);
    } catch (error) {
      console.error("[TalentLiveStream] Error ending session:", error);
    }
  };

  const handleEndCallClick = () => setShowEndDialog(true);

  const handleQualityChange = async (quality: "240p" | "360p" | "480p" | "720p") => {
    try {
      if (!zg || !isJoined) {
        setSnackbar({ open: true, message: "Not connected to stream", severity: "error" });
        return;
      }

      const qualityConfigs = {
        "240p": { width: 426, height: 240, bitrate: 300, fps: 10 },
        "360p": { width: 640, height: 360, bitrate: 800, fps: 20 },
        "480p": { width: 854, height: 480, bitrate: 1200, fps: 25 },
        "720p": { width: 1280, height: 720, bitrate: 3000, fps: 30 },
      };

      const config = qualityConfigs[quality];

      if (localStream) {
        await zg.setVideoConfig(localStream, {
          width: config.width,
          height: config.height,
          frameRate: config.fps,
        });
      }

      setVideoQuality(quality);
      setQualityAuto(false);
      setSnackbar({ open: true, message: `Video quality changed to ${quality}`, severity: "success" });
    } catch (error) {
      console.error("[TalentLiveStream] Quality change error:", error);
      setSnackbar({ open: true, message: "Failed to change video quality", severity: "error" });
    }
    setSettingsAnchor(null);
  };

  const toggleAudio = () => {
    try {
      if (!localStream) return;
      localStream.getAudioTracks().forEach((track) => (track.enabled = !track.enabled));
      setMic((prev) => !prev);
    } catch (error) {
      console.error("[TalentLiveStream] Toggle audio error", error);
    }
  };

  const toggleVideo = () => {
    try {
      if (!localStream) return;
      localStream.getVideoTracks().forEach((track) => (track.enabled = !track.enabled));
      setCamera((prev) => !prev);
    } catch (error) {
      console.error("[TalentLiveStream] Toggle video error", error);
    }
  };

  /* ─── render ─────────────────────────────────────────────────────────── */
  return (
    <>
      <MainContainer ref={fullscreenRef}>
        <VideoCallWrapper
          sx={{
            position: "relative",
            height: isFullscreen ? "100vh" : "auto",
            width: "100%",
            bgcolor: "#000",
            opacity: 1,
            transition: "all 0.3s ease-in-out",
          }}
        >
          <Box sx={{
            width: "100%",
            height: isFullscreen ? "100vh" : "100%",
            position: "relative",
            borderRadius: isFullscreen ? 0 : "8px",
            overflow: "hidden",
            // Neon stage border pulses when karaoke is active
            outline: karaokeActive ? "2px solid #ff6b9d" : "none",
            boxShadow: karaokeActive
              ? "0 0 0 3px rgba(255,107,157,0.25), 0 0 32px 8px rgba(168,85,247,0.22)"
              : "none",
            animation: karaokeActive ? "kBorder 2.5s ease-in-out infinite" : "none",
            "@keyframes kBorder": {
              "0%,100%": { boxShadow: "0 0 0 3px rgba(255,107,157,0.25), 0 0 32px 8px rgba(168,85,247,0.22)" },
              "50%":     { boxShadow: "0 0 0 4px rgba(34,211,238,0.35),  0 0 48px 12px rgba(34,211,238,0.20)" },
            },
            transition: "outline 0.5s ease, box-shadow 0.5s ease",
          }}>
            <video
              ref={localVideoRef}
              autoPlay
              playsInline
              width="100%"
              height="100%"
              style={{
                display: "block",
                width: "100%",
                height: "100%",
                objectFit: isFullscreen ? "contain" : "cover",
                backgroundColor: "#000",
                borderRadius: isFullscreen ? 0 : 8,
              }}
            />
            {/* Karaoke stage overlay — lights, notes, EQ, track name */}
            {karaokeActive && <KaraokeVideoOverlay trackName={karaokeTrackName} />}
          </Box>

          {/* ── Control bar — centered glassmorphism pill ── */}
          <Box
            sx={{
              position: "absolute",
              bottom: { xs: 12, sm: 18 },
              left: "50%",
              transform: "translateX(-50%)",
              zIndex: 1000,
              display: "inline-flex",   // shrink-wraps to content — no stretching
              alignItems: "center",
              gap: { xs: 0.5, sm: 0.75 },
              px: { xs: 1.25, sm: 1.75 },
              py: { xs: 0.75, sm: 1 },
              borderRadius: "999px",
              bgcolor: "rgba(10,10,10,0.75)",
              backdropFilter: "blur(20px)",
              border: "1px solid rgba(255,255,255,0.13)",
              boxShadow: "0 6px 32px rgba(0,0,0,0.6), 0 0 0 1px rgba(255,255,255,0.05) inset",
              whiteSpace: "nowrap",
              // Bottom gradient so pill contrasts well over any background
              "&::before": {
                content: '""',
                position: "absolute",
                inset: 0,
                borderRadius: "inherit",
                pointerEvents: "none",
              },
            }}
          >
            {/* ── Mic ── */}
            <Tooltip title={micOn ? "Mute microphone" : "Unmute microphone"} placement="top" arrow>
              <Box component="span">
                <ControlButton
                  onClick={toggleAudio}
                  icon={micOn ? <Mic sx={{ fontSize: 18 }} /> : <MicOff sx={{ fontSize: 18 }} />}
                  sx={{
                    ...controlButtonStyle,
                    backgroundColor: micOn ? "rgba(238,242,255,0.92)" : "rgba(229,9,20,0.85)",
                    color: micOn ? "#1a1a1a" : "#fff",
                    "&:hover": { backgroundColor: micOn ? "#fff" : "#b20710" },
                    transition: "background-color 0.2s",
                  }}
                />
              </Box>
            </Tooltip>

            {/* ── Camera ── */}
            <Tooltip title={cameraOn ? "Turn off camera" : "Turn on camera"} placement="top" arrow>
              <Box component="span">
                <ControlButton
                  onClick={toggleVideo}
                  icon={cameraOn ? <Videocam sx={{ fontSize: 18 }} /> : <VideocamOff sx={{ fontSize: 18 }} />}
                  sx={{
                    ...controlButtonStyle,
                    backgroundColor: cameraOn ? "rgba(238,242,255,0.92)" : "rgba(229,9,20,0.85)",
                    color: cameraOn ? "#1a1a1a" : "#fff",
                    "&:hover": { backgroundColor: cameraOn ? "#fff" : "#b20710" },
                    transition: "background-color 0.2s",
                  }}
                />
              </Box>
            </Tooltip>

            {/* ── Thin separator ── */}
            <Box sx={{ width: "1px", height: 22, bgcolor: "rgba(255,255,255,0.18)", mx: 0.5, flexShrink: 0 }} />

            {/* ── Fullscreen ── */}
            <Tooltip title={isFullscreen ? "Exit fullscreen" : "Enter fullscreen"} placement="top" arrow>
              <Box component="span">
                <ControlButton
                  onClick={toggleFullscreen}
                  icon={isFullscreen
                    ? <FullscreenExit sx={{ fontSize: 18 }} />
                    : <Fullscreen sx={{ fontSize: 18 }} />
                  }
                  sx={{
                    ...controlButtonStyle,
                    "&:hover": { backgroundColor: "#fff" },
                  }}
                />
              </Box>
            </Tooltip>

            {/* ── Quality / Settings ── */}
            <Tooltip title="Video quality" placement="top" arrow>
              <Box component="span">
                <ControlButton
                  onClick={(e) => e && setSettingsAnchor(e.currentTarget)}
                  icon={<Settings sx={{ fontSize: 18 }} />}
                  sx={{
                    ...controlButtonStyle,
                    "&:hover": { backgroundColor: "#fff" },
                  }}
                />
              </Box>
            </Tooltip>

            {/* ── Thin separator ── */}
            <Box sx={{ width: "1px", height: 22, bgcolor: "rgba(255,255,255,0.18)", mx: 0.5, flexShrink: 0 }} />

            {/* ── End session ── */}
            <Tooltip title="End / Leave session" placement="top" arrow>
              <Box component="span">
                <ControlButton
                  onClick={handleEndCallClick}
                  icon={<CallEnd sx={{ fontSize: 18 }} />}
                  sx={{
                    ...controlButtonStyleDestructive,
                    "&:hover": { backgroundColor: "#b20710" },
                    transition: "background-color 0.2s",
                  }}
                />
              </Box>
            </Tooltip>

            {/* ── Quality menu ── */}
            <Menu
              anchorEl={settingsAnchor}
              open={Boolean(settingsAnchor)}
              onClose={() => setSettingsAnchor(null)}
              anchorOrigin={{ vertical: "top", horizontal: "center" }}
              transformOrigin={{ vertical: "bottom", horizontal: "center" }}
              PaperProps={{
                sx: {
                  bgcolor: "#141414",
                  color: "#fff",
                  border: "1px solid rgba(255,255,255,0.12)",
                  borderRadius: 2,
                  minWidth: 160,
                },
              }}
            >
              {(["240p", "360p", "480p", "720p"] as const).map((q) => (
                <MenuItem
                  key={q}
                  onClick={() => handleQualityChange(q)}
                  selected={videoQuality === q && !qualityAuto}
                  sx={{ "&.Mui-selected": { bgcolor: "rgba(229,9,20,0.15)" } }}
                >
                  <ListItemIcon sx={{ color: "#fff" }}><Hd fontSize="small" /></ListItemIcon>
                  <ListItemText
                    primary={q}
                    secondary={videoQuality === q && !qualityAuto ? "Current" : ""}
                    secondaryTypographyProps={{ sx: { color: "rgba(255,255,255,0.5)" } }}
                  />
                </MenuItem>
              ))}
              <MenuItem
                onClick={async () => {
                  try {
                    await handleQualityChange("720p");
                    setQualityAuto(true);
                    setSnackbar({ open: true, message: "Auto quality enabled", severity: "success" });
                  } catch {}
                  setSettingsAnchor(null);
                }}
                selected={qualityAuto}
                sx={{ "&.Mui-selected": { bgcolor: "rgba(229,9,20,0.15)" } }}
              >
                <ListItemIcon sx={{ color: "#fff" }}><Settings fontSize="small" /></ListItemIcon>
                <ListItemText
                  primary="Auto"
                  secondary={qualityAuto ? "Current" : ""}
                  secondaryTypographyProps={{ sx: { color: "rgba(255,255,255,0.5)" } }}
                />
              </MenuItem>
            </Menu>
          </Box>

          <Snackbar
            open={snackbar.open}
            autoHideDuration={3000}
            onClose={() => setSnackbar({ ...snackbar, open: false })}
            message={snackbar.message}
            anchorOrigin={{ vertical: "top", horizontal: "center" }}
          />

          {/* End Session Dialog */}
          <Dialog
            open={showEndDialog}
            onClose={() => setShowEndDialog(false)}
            maxWidth="sm"
            fullWidth
          >
            <DialogTitle>
              <Typography variant="h6" component="div">End Session</Typography>
            </DialogTitle>
            <DialogContent>
              <Typography variant="body1" sx={{ mb: 2 }}>
                What would you like to do?
              </Typography>
              <Typography variant="body2" color="text.secondary">
                <strong>Leave:</strong> You will leave the session but it will continue for other participants.
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                <strong>End Session:</strong> The entire session will be ended for all participants.
              </Typography>
            </DialogContent>
            <DialogActions sx={{ p: 2, gap: 1 }}>
              <Button onClick={() => setShowEndDialog(false)} variant="outlined" color="primary">
                Cancel
              </Button>
              <Button onClick={handleLeaveSession} variant="outlined" color="warning">
                Leave
              </Button>
              <Button onClick={handleEndSession} variant="contained" color="error">
                End Session
              </Button>
            </DialogActions>
          </Dialog>
        </VideoCallWrapper>
      </MainContainer>
    </>
  );
}

/* ─── ControlButton ─────────────────────────────────────────────────────── */
const ControlButton = ({
  onClick,
  icon,
  sx = {},
}: {
  onClick: (e?: React.MouseEvent<HTMLButtonElement>) => void;
  icon: React.ReactNode;
  sx: Record<string, any>;
}) => (
  <IconButton color="error" onClick={onClick} sx={{ ...sx }}>
    {icon}
  </IconButton>
);
